<template>
  <div class="page-content">
    <div class="bg__wrapper">
      <v-container class="app-container--width-max">
        <div class="content content--padding-small">
          <div
            class="bg-info text-white ml-3 pa-5 ml-lg-10 pa-lg-4 pl-lg-10 pt-lg-6"
          >
            <span class="text-bold text--size-lg">Success!</span>
            <p>Your application is now complete.</p>
          </div>
        </div>
      </v-container>
    </div>

    <v-container class="block-info app-container--width-max">
      <div class="content content--padding-small">
        <v-card
          v-show="!isProcessingData"
          elevation="2"
          class="card__wrapper rounded-lg mt-10 mx-3 pa-4 pa-lg-15"
        >
          <v-alert color="var(--green-100)" class="rounded">
            <div class="d-flex flex-no-wrap">
              <div class="mr-3">
                <v-icon color="var(--green-500)">mdi-check-circle</v-icon>
              </div>
              <div>
                <span class="font-weight-bold"
                  >Application completed successfully!</span
                >
                Please find your application reference below.
              </div>
            </div>
          </v-alert>

          <v-alert
            v-if="isAdvisedSeller && !isSdnCreated"
            color="#FBEADA"
            class="rounded mt-3"
          >
            <div class="d-flex flex-no-wrap">
              <div class="mr-3">
                <v-icon color="#DF8430">mdi-alert</v-icon>
              </div>
              <div>
                <span class="font-weight-bold">Important, before you go.</span>
                Please create a Statement of Demands &amp; Needs.
                <a href="#" @click.prevent="showSdnDrawer"
                  ><u>Create one now.</u></a
                >
              </div>
            </div>
          </v-alert>

          <div class="font-18 mt-10">Application reference</div>
          <div class="reference-number mt-3">
            {{ getApplicationReference ?? quoteReference }}
          </div>

          <v-alert
            v-if="isAdvisedSeller"
            color="var(--blue-100)"
            class="mt-5 mb-0"
          >
            <div class="d-flex flex-no-wrap">
              <div class="mr-3">
                <v-icon color="var(--blue-500)">mdi-information</v-icon>
              </div>
              <div>
                <span class="font-weight-bold">Important!</span> Your premium
                and cover are based on the information we now hold for you and
                your home, so it’s important that, if at any time, you want to
                change any of the information you have previously given, or your
                circumstances change you tell us. If you don’t it may affect
                your policy cover, could lead to a potential claim not being
                paid and could also lead to cancellation of your policy.
              </div>
            </div>
          </v-alert>

          <LayoutApplicationSummaryCard
            @showPremiumBreakdownMonthly="showPremiumBreakdownMonthly"
            @showPremiumBreakdown3Months="showPremiumBreakdown3Months"
            @showPremiumBreakdownAnnual="showPremiumBreakdownAnnual"
            @shareQuote="shareQuote"
            @printQuote="printQuote"
            :useRetrievedPaymentInfo="$route.params.type === 'resumeQuote'"
            :showPayByCreditDebitCardMessage="false"
          />

          <div class="sdn-section">
            <v-container v-if="showSdnSection">
              <h5 class="font-18 mt-10">Statement of demands and needs</h5>

              <div
                v-if="canCreateSdn && !isSdnCreated"
                class="d-flex flex-column flex-lg-row"
              >
                <p class="my-2 ml-lg-4 mr-lg-10">
                  As your sale has been completed on an "Advised Basis" please
                  create a Statement of Demands &amp; Needs document to be
                  provided to your client.
                </p>
                <BaseButton
                  text="CREATE SDN"
                  flat
                  height="60"
                  :min-width="$vuetify.breakpoint.lgAndUp ? '256' : '100'"
                  rounded
                  depressed
                  :block="$vuetify.breakpoint.lgAndUp ? false : true"
                  class="py-6 mt-5 ml-lg-3 mt-xl-0 font-20 border-1"
                  iconColor="var(--red-500)"
                  @click="showSdnDrawer()"
                />
              </div>

              <div
                v-else-if="canCreateSdn && isStandardSdnCreated"
                class="d-flex flex-column flex-lg-row"
              >
                <v-icon class="d-none d-lg-flex" color="#1d8649"
                  >mdi-check-circle</v-icon
                >
                <p class="my-2 ml-lg-4 mr-lg-10">
                  You can now download the statement of demands and needs from
                  the documents section below.
                </p>
                <BaseButton
                  text="RECREATE SDN"
                  flat
                  height="60"
                  :min-width="$vuetify.breakpoint.lgAndUp ? '256' : '100'"
                  rounded
                  depressed
                  :block="$vuetify.breakpoint.lgAndUp ? false : true"
                  class="py-6 mt-5 ml-lg-3 mt-xl-0 font-20 border-1"
                  iconColor="var(--red-500)"
                  @click="showSdnDrawer()"
                />
              </div>

              <div v-else class="d-flex flex-column flex-lg-row">
                <v-icon class="d-none d-lg-flex" color="#1d8649"
                  >mdi-check-circle</v-icon
                >
                <p class="my-2 ml-lg-4 mr-lg-10">
                  You can now download the statement of demands and needs from
                  the documents section below.
                </p>
              </div>
            </v-container>
          </div>

          <h5 class="font-20 mt-6 font-weight-bold">Document selection</h5>

          <div class="col-12" v-if="isDownloadError" ref="downloadError">
            <v-alert
              dense
              text
              type="error"
              icon="mdi-alert-circle"
              class="ma-0 flex-grow-1 rounded-lg"
            >
              <span class="black--text">
                <strong>Sorry! </strong> Document could not be downloaded at
                this time.
              </span>
            </v-alert>
          </div>

          <div class="row mt-4" style="position: relative">
            <v-overlay absolute :value="isShowDocumentsOverlay">
              <v-progress-circular
                indeterminate
                size="64"
              ></v-progress-circular>
            </v-overlay>

            <div
              class="col-12 col-lg-6 px-lg-8"
              v-for="file in visibleFiles"
              :key="file.key"
            >
              <v-card
                elevation="0"
                :color="
                  file.isDownloaded ? 'bg-blue-grey-500' : 'bg-blue-grey-900'
                "
                :height="400"
                class="pa-4 rounded-lg d-flex flex-column"
              >
                <div class="d-flex justify-end">
                  <div style="position: relative">
                    <v-icon
                      v-if="!file.isDownloaded"
                      color="red"
                      style="position: absolute; z-index: 1; font-size: 12px"
                      >mdi-circle</v-icon
                    >
                    <v-icon color="white">mdi-file mdi-24px</v-icon>
                  </div>
                </div>
                <h5 class="font-24 font-weight-medium white--text mt-4">
                  {{ file.title }}
                </h5>
                <p class="white--text mt-4 mb-16 pb-lg-10 flex-grow-1">PDF</p>
                <div class="ma-6">
                  <BaseButton
                    text="DOWNLOAD"
                    height="60"
                    depressed
                    rounded
                    block
                    :color="
                      file.isDownloaded
                        ? 'btn-file-downloaded-text'
                        : 'psorange'
                    "
                    class="font-weight-bold"
                    className="btn-file-downloaded"
                    :textColor="
                      file.isDownloaded ||
                      (file.isDownloaded && file.isDownloading)
                        ? 'white--text'
                        : ''
                    "
                    :fontSize="
                      $vuetify.breakpoint.mdAndUp ? 'font-20' : 'font-18'
                    "
                    @click="downloadFile(file.key)"
                    :loading="file.isDownloading"
                    loadingText="DOWNLOADING..."
                    :loadingIconColor="
                      file.isDownloaded && file.isDownloading ? 'white' : ''
                    "
                  />
                </div>
              </v-card>
            </div>
          </div>

          <v-divider class="mt-5"></v-divider>

          <h5 class="font-18 mt-10">Questions?</h5>
          <p>
            You can check out our
            <a
              title="frequently asked questions"
              href="https://paymentshield.helpshift.com/hc/en/4-advisers/"
              target="_blank"
              class="text-decoration-underline teal--text text--darken-2"
              >frequently asked questions
              <v-icon size="medium">mdi-open-in-new</v-icon></a
            >
            for more information.
          </p>

          <h5 class="font-18 mt-16">Next steps</h5>
          <p>
            If you need to make a claim or cancel your policy, simply follow the
            procedure on your insurer schedule or contact our customer services
            team.
          </p>
          <p>
            We recommend you keep all information relating to your policy in a
            safe place to make sure you have all the relevant details if you
            need to contact us.
          </p>
        </v-card>
      </div>
      <DrawerDemandsAndNeedsAdvised
        v-if="isAdvisedSeller"
        v-model="isShowDrawerDemandsAndNeedsAdvised"
        :sdnData="SDN_TEMPLATE_ADVISED"
        @refreshDocs="getQuoteDocuments"
      ></DrawerDemandsAndNeedsAdvised>
      <DrawerDemandsAndNeeds
        v-else
        v-model="isShowDrawerDemandsAndNeeds"
        :sdnData="SDN_TEMPLATE"
        @refreshDocs="getQuoteDocuments"
      ></DrawerDemandsAndNeeds>
    </v-container>
    <BaseDialog
      ref="DialogContentPremiumBreakdown"
      windowClass="pa-0 pa-xl-4 pb-xl-0"
      headerClass="pa-4"
    >
      <template v-slot:content="{ data }">
        <DialogContentPremiumBreakdown
          :breakdownModel="data"
        ></DialogContentPremiumBreakdown>
      </template>
    </BaseDialog>
    <BaseDialog
      ref="shareDialog"
      headerIcon="mdi-share-variant"
      headerIconColor="#106175"
    >
      <template v-slot:content="{ data }">
        <DialogContentShareQuotes
          :content="getCover"
          :quoteId="data"
          :showSdn="isSdnCreated"
          :showSuitabilityReport="isSuitabilityReportCreated"
          @success="shareQuotesSuccess"
        ></DialogContentShareQuotes>
      </template>
    </BaseDialog>
    <v-overlay :value="overlay || !getApplicationReference">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
    <BaseDialog
      ref="printQuotes"
      headerIcon="mdi-printer"
      headerIconColor="#106175"
      headerDivider
      windowClass="pa-0 pb-xl-0 mb-0"
      headerClass="px-7 pt-6 pb-3"
      contentClass="mb-0"
    >
      <template v-slot:content="{ data }">
        <DialogContentPrintQuotes :data="data"></DialogContentPrintQuotes>
      </template>
    </BaseDialog>
  </div>
</template>

<script>
import PremiumBreakdownMixin from "@/Mixins/PremiumBreakdownMixin";
import { adBoolToString } from "@/common/cover";
import { filterSuccessQuoteDocs } from "@/common/docs";
import {
  addSuccessPageSortOrderToDocs,
  sortQuoteDocsBySortOrder,
} from "@/common/sorting";
import { applyThirdPartyId } from "@/common/utils";
import { ANNUAL } from "@/constants/common.constants";
import BaseButton from "@/components/BaseComponents/BaseButton.vue";
import LayoutApplicationSummaryCard from "@/components/LayoutComponents/LayoutApplicationSummaryCard";
import documentService from "@/services/document.service";
import {
  APPLICATION_MODULE,
  QUESTIONS_MODULE,
} from "@/store/types/module-types";
import { mapActions, mapGetters, mapState } from "vuex";
import { getNamePageSuccessByJourney } from "@/common/navigating";
import { SDN_TEMPLATE, SDN_TEMPLATE_ADVISED } from "@/constants/sdn.constants";

export default {
  name: "ApplicationSuccessPage",
  components: {
    BaseButton,
    LayoutApplicationSummaryCard,
    BaseDialog: () => import("@/components/BaseComponents/BaseDialog.vue"),
    DialogContentPremiumBreakdown: () =>
      import("@/components/QuoteResult/DialogContentPremiumBreakdown.vue"),
    DrawerDemandsAndNeeds: () =>
      import("@/components/QuoteResult/DrawerDemandsAndNeeds.vue"),
    DrawerDemandsAndNeedsAdvised: () =>
      import("@/components/QuoteResult/DrawerDemandsAndNeedsAdvised.vue"),
    DialogContentShareQuotes: () =>
      import("@/components/QuoteResult/DialogContentShareQuotes.vue"),
    DialogContentPrintQuotes: () =>
      import("@/components/QuoteResult/DialogContentPrintQuotes.vue"),
  },
  mixins: [PremiumBreakdownMixin],
  data() {
    return {
      SDN_TEMPLATE,
      SDN_TEMPLATE_ADVISED,

      quoteDocuments: [],
      files: [
        {
          title: "Suitability report",
          key: "suitability",
          isVisible: false,
          isDownloaded: false,
          isDownloading: false,
        },
        {
          title: "Quote and Application Summary",
          key: "qas",
          isVisible: true,
          isDownloaded: false,
          isDownloading: false,
        },
        {
          title: "Statement of Demands and Needs",
          key: "sdn",
          isVisible: false,
          isDownloaded: false,
          isDownloading: false,
        },
        {
          title: "Statement of Demands and Needs",
          key: "factfindsdn",
          isVisible: false,
          isDownloaded: false,
          isDownloading: false,
        },
      ],
      isShowDrawerDemandsAndNeeds: false,
      isShowDrawerDemandsAndNeedsAdvised: false,
      isShowDocumentsOverlay: false,
      overlay: false,
      documentList: [],
      isDownloadError: false,
      isProcessingData: true,
    };
  },
  computed: {
    ...mapGetters(APPLICATION_MODULE, [
      "getApplicationReference",
      "getAnswerFromApplyResponse",
      "quoteId",
      "selectedQuote",
    ]),
    ...mapGetters(QUESTIONS_MODULE, [
      "quoteReference",
      "getQuoteRequestId",
      "httpConfig",
      "insurerQuotesModel",
      "getCover",
      "isQuilterFactFind",
      "isAdvisedSeller",
      "callChannel",
      "isSelfServe",
      "productId",
    ]),
    ...mapState(QUESTIONS_MODULE, ["userId", "questionMap"]),
    currentRouteName() {
      return this.$route.name;
    },
    visibleFiles() {
      return this.files.filter((f) => f.isVisible);
    },
    canCreateSdn() {
      const basisOfAdvice = this.getAnswerFromApplyResponse("BasisOfAdvice");
      return basisOfAdvice === "Advised";
    },
    showSdnSection() {
      // Quote Docs response has returned and there is no OW Suitability doc
      return (
        this.quoteDocuments.length > 0 &&
        !this.files.some((f) => f.isVisible && f.key === "suitability")
      );
    },
    isSdnCreated() {
      // Quote docs response has returned and there is an SDN
      return (
        this.quoteDocuments.length > 0 &&
        this.files.some(
          (f) => f.isVisible && (f.key === "sdn" || f.key === "factfindsdn")
        )
      );
    },
    isSuitabilityReportCreated() {
      // Quote docs response has returned and there is an Suitability Report
      return (
        this.quoteDocuments.length > 0 &&
        this.files.some((f) => f.isVisible && f.key === "suitability")
      );
    },
    isStandardSdnCreated() {
      return (
        this.quoteDocuments.length > 0 &&
        this.files.some((f) => f.isVisible && f.key === "sdn")
      );
    },
    paymentFrequency() {
      return this.questionMap.PaymentFrequency.Value;
    },
    paymentFrequencyForDocs() {
      // Prevent returning an empty string for Payment Frequency if the payment method is card.
      return this.questionMap.PaymentMethod.Value === "CreditCard"
        ? ANNUAL
        : this.paymentFrequency;
    },
  },
  async beforeMount() {
    const paymentMethod = sessionStorage.getItem("paymentMethod");
    if (paymentMethod === "CreditCard") {
      await Promise.all([
        this.retrieveQuoteFollowingSuccessfulCardPayment(),
        this.repopulateActiveQuoteFromSessionStorage(),
      ]);
      sessionStorage.removeItem("paymentMethod");
      applyThirdPartyId(this.isSelfServe, this.userId);
      const successPageName = getNamePageSuccessByJourney();
      if (successPageName && successPageName !== this.currentRouteName) {
        this.$router.push({
          name: successPageName,
          params: {
            type: "resumeQuote",
            quoteRequestId: this.getQuoteRequestId,
            syncQuoteRequest: true,
          },
        });
        return;
      }
    } else if (paymentMethod === "DirectDebit") {
      // remove payment method from storage so that if the page is refreshed, user is redirected to adviser hub.
      sessionStorage.removeItem("paymentMethod");
      //Params is used when retrieving a quote. If no params, redirect to adviser hub.
    } else if (!this.$route.params.type) {
      this.exitToAdviserHub("submitted_quotes");
      return;
    }
    this.isProcessingData = false;
    // get quote documents moved here to ensure state restoration when redirecting from card payment is done
    this.getQuoteDocuments();
  },
  mounted() {
    window.scroll({
      top: 0,
      behavior: "smooth",
    });

    window.dataLayer.push({
      event: "purchase",
      ecommerce: {
        purchase: {
          transaction_id: this.getApplicationReference ?? this.quoteReference,
          value: this.selectedQuote.AnnualTotal.toString(),
          currency: "GBP",
          items: [
            {
              item_name: this.selectedQuote.ProductName,
              item_id: this.productId.toString(),
              price: this.selectedQuote.AnnualTotal.toString(),
              item_brand: this.selectedQuote.InsurerName,
              quantity: "1",
            },
          ],
        },
      },
    });

    // add beforeunload listener
    if (this.isAdvisedSeller && !this.isSdnCreated) {
      window.addEventListener("beforeunload", this.handleBeforeUnload);
    }
  },
  methods: {
    ...mapActions(APPLICATION_MODULE, [
      "retrieveQuoteByQuoteRequestIdFollowingSuccessfulCardPayment",
      "repopulateActiveQuoteFromSessionStorage",
    ]),
    ...mapActions(QUESTIONS_MODULE, ["exitToAdviserHub"]),
    showSdnDrawer() {
      if (this.isAdvisedSeller) {
        this.isShowDrawerDemandsAndNeedsAdvised = true;
      } else {
        this.isShowDrawerDemandsAndNeeds = true;
      }
    },
    shareQuote(quoteId) {
      this.$refs.shareDialog.open("Share via email", quoteId);
    },
    shareQuotesSuccess() {
      this.$refs.shareDialog.cancel();
    },
    async getQuoteDocuments() {
      this.files.find((file) => file.key === "sdn").isDownloaded = false;
      this.documentList = [];
      this.isShowDocumentsOverlay = true;
      documentService
        .getQuoteDocuments(this.quoteId, this.callChannel, this.httpConfig)
        .then((res) => {
          this.quoteDocuments = res.Documents;
          this.isShowDocumentsOverlay = false;
          this.files.forEach((f) => {
            const matchedDocument = res.Documents.find(
              (doc) => doc.Code === f.key
            );
            f.isVisible = Boolean(matchedDocument);
            f.title = matchedDocument?.Name ? matchedDocument.Name : f.title;
          });

          // remove beforeunload listener
          if (this.isAdvisedSeller && this.isSdnCreated) {
            window.removeEventListener("beforeunload", this.handleBeforeUnload);
          }
        })
        .catch((err) => {
          console.log(err);
          this.isShowDocumentsOverlay = false;
          this.isDownloadError = true;
        });
    },
    async retrieveQuoteFollowingSuccessfulCardPayment() {
      await this.retrieveQuoteByQuoteRequestIdFollowingSuccessfulCardPayment();
    },
    downloadFile(documentCode) {
      // `file` is the matching entry from the API response
      const file = this.quoteDocuments.find(
        (document) => document.Code === documentCode
      );

      if (!file) {
        this.isDownloadError = true;
        return;
      }

      this.isDownloadError = false;

      // `theDoc` is our local data model that holds the download status
      const theDoc = this.files.find((f) => f.key == documentCode);
      if (theDoc.isDownloading) return;
      theDoc.isDownloading = true;

      documentService
        .getDocument(file.Link, this.httpConfig)
        .then((res) => {
          window.open(URL.createObjectURL(res));
          theDoc.isDownloading = false;
          theDoc.isDownloaded = true;
        })
        .catch((error) => {
          theDoc.isDownloading = false;
          theDoc.isDownloaded = false;
          this.isDownloadError = true;
          setTimeout(() => {
            const el = this.$refs.downloadError;
            if (el) {
              el.scrollIntoView({ behavior: "smooth" });
            }
          }, 500);
          console.error(error);
        });
    },
    getDocumentList() {
      return new Promise((resolve, reject) => {
        if (this.documentList.length) {
          resolve(this.documentList);
          return;
        }
        documentService
          .getDocumentList(
            this.getQuoteRequestId,
            this.callChannel,
            this.httpConfig
          )
          .then((res) => {
            if (res.StatusCode === "OK") {
              res.Documents.forEach((doc) => {
                doc.isDownloading = false;
                doc.isDownloaded = false;
              });

              //The code below is to have a copy of these files for each quote so we could track their download state.
              //only two records of ipid and terms are coming back from api, we need one for each quote for their download state.
              const termFile = res.Documents.find(
                (doc) => doc.Code === "terms"
              );
              const ipidFile = res.Documents.find((doc) => doc.Code === "ipid");
              this.insurerQuotesModel.forEach((quote) => {
                if (quote.isAvailable) {
                  quote.quotes.forEach((insurerQuote) => {
                    res.Documents.push({
                      ...termFile,
                      QuoteId: insurerQuote.QuoteId,
                    });
                    res.Documents.push({
                      ...ipidFile,
                      QuoteId: insurerQuote.QuoteId,
                    });
                  });
                }
              });
              this.documentList = res.Documents;
              resolve(this.documentList);
            }
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    printQuote(quote) {
      this.overlay = true;
      this.getDocumentList()
        .then(() => {
          const data = {
            ...quote,
            files: this.documentList
              .filter((doc) =>
                filterSuccessQuoteDocs(
                  doc,
                  quote.QuoteId,
                  {
                    contentsAd: adBoolToString(quote.ContentsAD),
                    buildingsAd: adBoolToString(quote.BuildingsAD),
                  },
                  this.paymentFrequencyForDocs
                )
              )
              .map(addSuccessPageSortOrderToDocs)
              .sort(sortQuoteDocsBySortOrder),
            type: "multi",
          };
          this.overlay = false;
          this.$refs.printQuotes.open("Download for printing", data);
        })
        .catch((error) => {
          this.overlay = false;
          console.error(error);
        });
    },
    handleBeforeUnload(e) {
      // recommended by MDN
      e.preventDefault();
      e.returnValue = true;
    },
  },
};
</script>

<style lang="scss" scoped>
.bg {
  &__wrapper {
    background-image: url(~@/assets/images/payment_success_background.jpg);
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
    height: 350px;
    width: 100%;
  }

  &-info {
    position: absolute;
    width: 540px !important;
    height: 140px;
    top: 30px;
  }
}

.block-info {
  margin-top: -165px;
  margin-bottom: 52px;
}

.card {
  height: 400px;
  &__wrapper {
    padding: 40px 44px 68px;
  }
}

hr {
  max-width: calc(100% + 373px);
  width: calc(100% + 120px);
  margin-left: -60px;
}

@media screen and (max-width: ($grid-breakpoints-md - 1)) {
  .bg {
    &__wrapper {
      height: 230px;
    }

    &-info {
      top: 20px;
      width: 340px !important;
    }
  }

  .card {
    &__wrapper {
      padding: 24px 25px 43px;
    }
  }

  .block-info {
    margin-top: -90px;
    margin-bottom: 47px;
  }

  hr {
    max-width: calc(100% + 59px);
    width: calc(100% + 32px);
    margin-left: -16px;
  }
}

.bg-info {
  opacity: 0.8;
  border-radius: 4px;
  background-color: #272727;
  span {
    width: 233px;
    height: 63px;
    flex-grow: 0;
    font-family: Montserrat;
    font-size: 52px;
    font-weight: bold;
    font-stretch: normal;
    font-style: normal;
    line-height: normal;
    letter-spacing: normal;
    text-align: left;
    color: #fab519;
  }

  p {
    height: 44px;
    flex-grow: 0;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
    font-family: Montserrat;
    font-size: 18px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: normal;
    letter-spacing: normal;
    text-align: left;
    color: #fff;
  }
}

.reference-number {
  font-size: 30px;
  font-weight: bold;
  color: #007185;
}

.quote {
  background-color: #354048;
}

.insurer-logo {
  border: solid 1px #7c869a;
  background-color: #fff;
}

.price {
  border: solid 1px #7c869a;
  background-color: #eff0f0;
  border-left: none;
}

@media screen and (max-width: ($grid-breakpoints-md - 1)) {
  .price {
    border-bottom-right-radius: 8px;
  }
}

.border-1 {
  border: solid 1px #7c869a;
}

.share-print {
  border: solid 1px #7c869a;
  border-left: none;
  border-bottom-right-radius: 8px;
}
@media screen and (max-width: ($grid-breakpoints-md - 1)) {
  .share-print {
    border: none;
    border-left: none;
  }
}

.btn-file-downloaded {
  border: 1px solid white;
}

.btn-file-downloaded-text {
  background-color: $blue-grey-500 !important;
}

.sdn-section {
  min-height: 131px;
  padding: 1px 0;
}
</style>
